import { Tag } from 'types/lead-management';
import { TabPill } from 'types/tabs';
import { Step } from 'types/vertical-stepper';

import { SortDropdownOption } from './components/event-sort-dropdown';
import { Question, QuestionType } from './types';

export enum EventListTab {
  ALL,
  ONGOING,
  COMPLETED,
  CANCELLED
}

export const eventTabs: TabPill[] = [
  { id: EventListTab.ALL, label: 'All' },
  { id: EventListTab.ONGOING, label: 'Ongoing' },
  { id: EventListTab.COMPLETED, label: 'Completed' },
  { id: EventListTab.CANCELLED, label: 'Cancelled' }
];

export const eventSortOptions: SortDropdownOption[] = [
  {
    label: 'Start Date',
    value: 'start_date',
    subOptions: [
      { label: 'Asc', optionLabel: 'Ascending', value: 'asc' },
      { label: 'Desc', optionLabel: 'Descending', value: 'desc' }
    ]
  },
  { label: 'Registration Close Date', value: 'registration_end_date' },
  { label: 'Registration Start Date', value: 'registration_start_date' },
  { label: 'Alphabetically (A-Z)', value: 'name' }
];

export const eventAttendeTableColumn = [
  { Header: 'Name', accessor: 'name' },
  {
    Header: 'Mobile Number',
    accessor: 'mobile'
  },
  {
    Header: 'Booking ID',
    accessor: 'bookingId'
  },
  // { Header: 'Quantity', accessor: 'quantity' },
  { Header: 'Ticket Name', accessor: 'ticketName' },
  { Header: 'Ticket Price', accessor: 'ticketPrice' },
  {
    Header: 'Payment Status',
    accessor: 'paymentStatus',
    width: '12.5%'
  },
  { Header: 'Check In Status', accessor: 'checkInStatus' }
];

export const InputTypeConfig = {
  [QuestionType.TEXT]: {
    label: 'Text Input',
    icon: 'text',
    placeholder: 'Enter your answer here'
  },
  [QuestionType.TEXT_AREA]: {
    label: 'Text Area',
    icon: 'text-area',
    placeholder: 'Enter your answer here'
  },
  [QuestionType.NUMBER]: {
    label: 'Number',
    icon: 'number-input',
    placeholder: 'Enter your answer here'
  },

  [QuestionType.MOBILE_NUMBER]: {
    label: 'Mobile Number',
    icon: 'number-input',
    placeholder: 'Enter your answer here'
  },
  [QuestionType.SINGLE_CHOICE]: {
    label: 'Single Choice',
    icon: 'single-choice',
    placeholder: 'Enter your answer here'
  },
  [QuestionType.MULTIPLE_CHOICE]: {
    label: 'Multiple Choice',
    icon: 'multiple-choice',
    placeholder: 'Enter your answer here'
  },
  [QuestionType.DROP_DOWN]: {
    label: 'Drop Down',
    icon: 'down',
    placeholder: 'Enter your answer here'
  },
  [QuestionType.DATE]: {
    label: 'Date',
    icon: 'calendar',
    placeholder: 'Enter your answer here'
  },
  [QuestionType.FILE_UPLOAD]: {
    label: 'File Upload',
    icon: 'file-upload',
    placeholder: 'Enter your answer here'
  },
  [QuestionType.EMAIL_ID]: {
    label: 'Email',
    icon: '',
    placeholder: 'Enter your answer here'
  }
};

export const maxNumberOfOptions = 15;
export const maxNumberOfOptionsPerQuestion = 300;

export const maxFileSize = 10;
export const minFileSize = 1;

export const textAreaMaxLength = 500;

export enum EventCreationSteps {
  DETAILS = 'details',
  BASIC_DETAILS = 'basic',
  OTHER_DETAILS = 'other',
  TICKETS = 'tickets',
  DISCOUNTS = 'discounts',
  QUESTIONS = 'questions',
  PREVIEW = 'preview'
}

export const EVENT_CREATION_STEPS = (
  isEventCouponsEnabled: boolean
): Step[] => [
  {
    id: EventCreationSteps.DETAILS,
    label: 'Event Details',
    completed: false,
    hasSubsteps: true,
    substeps: [
      {
        id: EventCreationSteps.BASIC_DETAILS,
        label: 'Basic Details',
        completed: false
      },
      {
        id: EventCreationSteps.OTHER_DETAILS,
        label: 'Other Details',
        completed: false
      }
    ]
  },
  {
    id: EventCreationSteps.TICKETS,
    label: 'Tickets',
    completed: false,
    hasSubsteps: false
  },
  ...(isEventCouponsEnabled
    ? [
        {
          id: EventCreationSteps.DISCOUNTS,
          label: 'Discounts',
          completed: false,
          hasSubsteps: false
        }
      ]
    : []),
  {
    id: EventCreationSteps.QUESTIONS,
    label: 'Registration Form',
    completed: false,
    hasSubsteps: false
  },
  {
    id: EventCreationSteps.PREVIEW,
    label: 'Preview & Publish',
    completed: false,
    hasSubsteps: false
  }
];

export enum DefaultQuestionNames {
  NAME = 'Name',
  MOBILE_NUMBER = 'Mobile Number',
  EMAIL_ID = 'Email ID'
}

export const defaultQuestions: Question[] = [
  {
    id: '1',
    question: DefaultQuestionNames.NAME,
    type: QuestionType.TEXT,
    required: true,
    questionMetaData: null,
    isDefault: true,
    displayOrder: 1
  },
  {
    id: '2',
    question: DefaultQuestionNames.MOBILE_NUMBER,
    type: QuestionType.MOBILE_NUMBER,
    required: true,
    questionMetaData: null,
    isDefault: true,
    displayOrder: 2
  },
  {
    id: '3',
    question: DefaultQuestionNames.EMAIL_ID,
    type: QuestionType.EMAIL_ID,
    required: true,
    questionMetaData: null,
    isDefault: true,
    displayOrder: 3
  }
];

export const defaultQuestionNames = defaultQuestions.map(
  (question) => question.question
);

export const fileUploadOptions = [
  {
    key: 'JPG, PNG',
    value: 'Image'
  },
  {
    key: 'PDF, DOC',
    value: 'Document'
  }
];

export enum TicketTypes {
  FREE = 'free',
  PAID = 'paid'
}

export const TicketTypeOptions = [
  { key: TicketTypes.FREE, value: 'Free' },
  { key: TicketTypes.PAID, value: 'Paid' }
];

export const DEFAULT_SUB_TICKET_TAG: Tag = {
  id: 'custom-default-event-check-in',
  name: 'Event check in',
  color: '#F5FCF8'
};

export const couponCodeMinLength = 3;
export const couponCodeMaxLength = 20;
